import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, ClipboardCheck, RotateCcw, Loader2, AlertTriangle, Archive } from "lucide-react";
import { HistoryDeltaHeadline, CategoryDelta, useHistory, buildPrevCategoryMap } from "@/components/HistoryDelta";
import RedFlagSummary from "@/components/RedFlagSummary";

// Map color tokens from the master rubric to Tailwind classes.
const COLOR_PILL = {
  red:         { bg: "bg-warn-soft",      text: "text-warn",  border: "border-warn/40" },
  orange:      { bg: "bg-warn-soft",      text: "text-warn",  border: "border-warn/40" },
  amber:       { bg: "bg-warn-soft",      text: "text-warn",  border: "border-warn/40" },
  light_green: { bg: "bg-good-soft",      text: "text-good",  border: "border-good/40" },
  dark_green:  { bg: "bg-good-soft",      text: "text-good",  border: "border-good/40" },
};
const COLOR_BAR = {
  red: "bg-warn", orange: "bg-warn", amber: "bg-warn",
  light_green: "bg-good", dark_green: "bg-good",
};
const PILL_FOR = (c) => COLOR_PILL[c] || COLOR_PILL.amber;
const BAR_FOR = (c) => COLOR_BAR[c] || "bg-brand";

/**
 * BNBD module — full diagnostic lifecycle inside the tender detail tab.
 * States: idle → start | view | retake → questionnaire → results
 */
export default function BnbdModule({ tenderId }) {
  const [schema, setSchema] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [archivedCount, setArchivedCount] = useState(0);
  const [view, setView] = useState("loading");   // loading | gate | quiz | results
  const [qIdx, setQIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showRetakeModal, setShowRetakeModal] = useState(false);

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const [{ data: sch }, { data: latest }] = await Promise.all([
          api.get("/bnbd/schema"),
          api.get(`/tenders/${tenderId}/bnbd-v2`),
        ]);
        setSchema(sch);
        setArchivedCount(latest.archived_count || 0);
        if (latest.assessment) {
          setAssessment(latest.assessment);
          setView(latest.assessment.status === "completed" ? "results" : "gate");
        } else {
          setView("gate");
        }
      } catch (ex) {
        toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
      }
    })();
  }, [tenderId]);

  // ---- Quiz helpers --------------------------------------------------------
  const answers = assessment?.answers || {};
  const currentQ = schema?.questions?.[qIdx];
  const totalQ = schema?.questions?.length || 20;
  const currentCategoryQs = useMemo(() => {
    if (!schema || !currentQ) return [];
    return schema.questions.filter((q) => q.category === currentQ.category);
  }, [schema, currentQ]);
  const currentCategoryLabel = useMemo(() => {
    if (!schema || !currentQ) return "";
    return schema.categories.find((c) => c.id === currentQ.category)?.label || "";
  }, [schema, currentQ]);
  const currentCategoryQNumber = useMemo(() => {
    if (!currentCategoryQs.length || !currentQ) return 1;
    return currentCategoryQs.findIndex((q) => q.num === currentQ.num) + 1;
  }, [currentCategoryQs, currentQ]);

  const selectAnswer = useCallback(async (questionNum, optionIdx) => {
    if (!assessment) return;
    const next = { ...(assessment.answers || {}), [String(questionNum)]: optionIdx };
    // Optimistic update for snappiness; backend reconciles.
    setAssessment({ ...assessment, answers: next });
    setSaving(true);
    try {
      const { data } = await api.patch(`/tenders/${tenderId}/bnbd-v2/answers`, {
        answers: { [String(questionNum)]: optionIdx },
      });
      setAssessment(data);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setSaving(false); }
  }, [assessment, tenderId]);

  const goNext = () => {
    if (qIdx < totalQ - 1) setQIdx(qIdx + 1);
  };
  const goPrev = () => { if (qIdx > 0) setQIdx(qIdx - 1); };

  const startAttempt = async () => {
    try {
      const { data } = await api.post(`/tenders/${tenderId}/bnbd-v2/start`);
      setAssessment(data);
      // Resume on first unanswered question.
      const firstUnanswered = (schema.questions || []).findIndex((q) => !(String(q.num) in (data.answers || {})));
      setQIdx(firstUnanswered === -1 ? 0 : firstUnanswered);
      setView("quiz");
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/tenders/${tenderId}/bnbd-v2/submit`, { answers: {} });
      setAssessment(data);
      setView("results");
      toast.success(`BNBD complete · ${data.total_score}/100 · ${data.tier_label}`);
    } catch (ex) {
      const detail = ex.response?.data?.detail;
      if (detail?.code === "incomplete" && Array.isArray(detail.missing_questions)) {
        toast.error(`Please answer all 20 questions. Missing: Q${detail.missing_questions.join(", Q")}`);
        // Jump to first missing question
        const idx = (schema.questions || []).findIndex((q) => detail.missing_questions.includes(q.num));
        if (idx >= 0) setQIdx(idx);
      } else {
        toast.error(formatApiErrorDetail(detail) || ex.message);
      }
    } finally { setSubmitting(false); }
  };

  const retakeConfirmed = async () => {
    setShowRetakeModal(false);
    try {
      const { data } = await api.post(`/tenders/${tenderId}/bnbd-v2/retake`);
      setAssessment(data);
      setArchivedCount((c) => c + 1);
      setQIdx(0);
      setView("quiz");
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  // ---- Loading -------------------------------------------------------------
  if (view === "loading") {
    return <div className="text-sm text-ink-muted uppercase tracking-wider" data-testid="bnbd-loading">Loading BNBD…</div>;
  }

  // ---- Gate (no draft / completed) -----------------------------------------
  if (view === "gate") {
    const hasCompleted = assessment?.status === "completed";
    const hasDraft = assessment?.status === "in_progress";
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fade-up" data-testid="bnbd-gate">
        <div className="lg:col-span-2 card-flat p-7">
          <div className="stat-label">BNBD</div>
          <h2 className="font-display text-3xl font-bold tracking-tight mt-1">Bid / No-Bid Diagnostic</h2>
          <p className="text-sm text-ink-muted mt-2 leading-relaxed max-w-prose">
            20 single-select questions across 4 strategic categories. Estimated 8–12 minutes.
            Your selections are auto-saved — you can step away and resume later.
          </p>

          {/* Categories preview */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
            {schema?.categories?.map((c) => (
              <div key={c.id} className="border border-ink-line p-3 rounded-sm bg-white">
                <div className="stat-label">{c.label}</div>
                <div className="font-num text-xs text-ink-muted mt-1">5 questions · max {c.weight} pts</div>
              </div>
            ))}
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            {hasDraft && (
              <button
                onClick={() => { const i = (schema?.questions || []).findIndex((q) => !(String(q.num) in (assessment.answers || {}))); setQIdx(i === -1 ? 0 : i); setView("quiz"); }}
                data-testid="bnbd-resume-btn"
                className="px-5 py-2.5 text-sm bg-brand hover:bg-brand-hover text-white font-medium rounded-sm flex items-center gap-2 transition-colors"
              >
                Resume <ArrowRight className="w-4 h-4" />
              </button>
            )}
            {!hasDraft && !hasCompleted && (
              <button
                onClick={startAttempt}
                data-testid="bnbd-start-btn"
                className="px-5 py-2.5 text-sm bg-brand hover:bg-brand-hover text-white font-medium rounded-sm flex items-center gap-2 transition-colors"
              >
                <ClipboardCheck className="w-4 h-4" /> Start assessment
              </button>
            )}
            {hasCompleted && (
              <>
                <button onClick={() => setView("results")} data-testid="bnbd-view-result-btn"
                        className="px-5 py-2.5 text-sm bg-brand hover:bg-brand-hover text-white font-medium rounded-sm flex items-center gap-2 transition-colors">
                  View latest result <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={() => setShowRetakeModal(true)} data-testid="bnbd-retake-btn"
                        className="px-4 py-2.5 text-sm border border-ink-line hover:bg-secondary rounded-sm flex items-center gap-2 transition-colors">
                  <RotateCcw className="w-4 h-4" /> Retake
                </button>
              </>
            )}
          </div>

          {hasDraft && (
            <div className="mt-4 text-xs text-ink-muted">
              Draft in progress — {Object.keys(assessment.answers || {}).length} of {totalQ} answered.
            </div>
          )}
        </div>

        {/* Right rail */}
        <div className="card-flat p-6 flex flex-col">
          <div className="stat-label">How it works</div>
          <ol className="text-sm space-y-2 mt-3 list-decimal pl-4 text-ink-muted">
            <li>Pick one of five options per question.</li>
            <li>Scores are not shown while you answer.</li>
            <li>On submit, you'll see a tier (BID / BID WITH CAUTION / NO-BID), category breakdown, and risk concentration.</li>
            <li>Result is attached to this tender automatically.</li>
          </ol>
          {archivedCount > 0 && (
            <div className="mt-6 pt-4 border-t border-ink-line text-xs text-ink-muted flex items-center gap-2" data-testid="bnbd-archived-count">
              <Archive className="w-3 h-3" /> {archivedCount} archived attempt{archivedCount === 1 ? "" : "s"}
            </div>
          )}
        </div>

        {showRetakeModal && <RetakeModal previousScore={assessment?.total_score} previousTier={assessment?.tier_label}
                                          onConfirm={retakeConfirmed} onClose={() => setShowRetakeModal(false)} />}
      </div>
    );
  }

  // ---- Quiz ---------------------------------------------------------------
  if (view === "quiz" && currentQ) {
    const answeredCount = Object.keys(answers).length;
    const selectedIdx = answers[String(currentQ.num)];
    const allAnswered = answeredCount === totalQ;
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fade-up" data-testid="bnbd-quiz">
        <div className="lg:col-span-2 card-flat p-7">
          {/* Progress */}
          <div className="flex items-center justify-between mb-4">
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">
              {currentCategoryLabel} · Question {currentCategoryQNumber} of {currentCategoryQs.length}
            </div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-num text-ink-muted" data-testid="bnbd-progress-text">
              Q{qIdx + 1} / {totalQ}
            </div>
          </div>
          <div className="h-1 bg-secondary rounded-sm overflow-hidden">
            <div className="h-full bg-brand transition-all" style={{ width: `${((qIdx + 1) / totalQ) * 100}%` }} data-testid="bnbd-progress-bar" />
          </div>

          {/* Question */}
          <h3 className="font-display text-xl font-bold tracking-tight mt-6" data-testid="bnbd-q-label">
            {currentQ.label}
          </h3>

          <div className="mt-5 space-y-2.5">
            {currentQ.options.map((o, i) => {
              const isSel = selectedIdx === i;
              return (
                <button
                  key={`${currentQ.num}-${i}`}
                  type="button"
                  onClick={() => selectAnswer(currentQ.num, i)}
                  data-testid={`bnbd-q${currentQ.num}-opt-${i}`}
                  className={`w-full flex items-center gap-3 text-left px-4 py-3 text-sm rounded-sm border transition-colors ${
                    isSel
                      ? "border-brand bg-brand/5 text-ink"
                      : "border-ink-line bg-white hover:bg-secondary text-ink-muted hover:text-ink"
                  }`}
                >
                  <span className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${
                    isSel ? "border-brand bg-brand text-white" : "border-ink-line"
                  }`}>
                    {isSel && <Check className="w-3 h-3" />}
                  </span>
                  <span>{o.label}</span>
                </button>
              );
            })}
          </div>

          <div className="mt-7 pt-5 border-t border-ink-line flex items-center justify-between flex-wrap gap-3">
            <button
              onClick={goPrev}
              disabled={qIdx === 0}
              data-testid="bnbd-back-btn"
              className="px-4 py-2 text-sm border border-ink-line hover:bg-secondary rounded-sm flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <div className="text-xs text-ink-muted">
              {saving && <span className="flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>}
              {!saving && selectedIdx === undefined && <span>Pick an option to continue.</span>}
              {!saving && selectedIdx !== undefined && <span>Saved.</span>}
            </div>
            {qIdx < totalQ - 1 ? (
              <button
                onClick={goNext}
                disabled={selectedIdx === undefined}
                data-testid="bnbd-next-btn"
                className="px-5 py-2 text-sm bg-brand hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-sm flex items-center gap-1.5 transition-colors"
              >
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={!allAnswered || submitting}
                data-testid="bnbd-submit-btn"
                className="px-5 py-2 text-sm bg-brand hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-sm flex items-center gap-1.5 transition-colors"
              >
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Submit <Check className="w-4 h-4" /></>}
              </button>
            )}
          </div>
        </div>

        {/* Right rail — progress dots */}
        <div className="card-flat p-6">
          <div className="stat-label">Progress</div>
          <div className="font-num text-5xl font-bold mt-2 text-brand">
            {answeredCount}<span className="text-ink-muted">/{totalQ}</span>
          </div>
          <div className="text-[11px] uppercase tracking-wider text-ink-muted mt-1">Answered</div>

          <div className="mt-5 grid grid-cols-5 gap-1.5" data-testid="bnbd-dots">
            {schema.questions.map((q, i) => {
              const answered = String(q.num) in answers;
              const isCurrent = i === qIdx;
              return (
                <button
                  key={q.num}
                  onClick={() => setQIdx(i)}
                  className={`aspect-square text-[10px] font-num rounded-sm transition-all ${
                    isCurrent
                      ? "bg-brand text-white ring-2 ring-brand/30"
                      : answered
                      ? "bg-good text-white hover:bg-good/80"
                      : "bg-secondary text-ink-muted hover:bg-ink-line"
                  }`}
                  data-testid={`bnbd-dot-${q.num}`}
                >
                  {q.num}
                </button>
              );
            })}
          </div>

          <div className="mt-6 pt-5 border-t border-ink-line text-[11px] text-ink-muted leading-relaxed">
            Scores are intentionally hidden while you answer. You'll see the full breakdown on the results screen.
          </div>
        </div>
      </div>
    );
  }

  // ---- Results ------------------------------------------------------------
  if (view === "results" && assessment?.status === "completed") {
    return (
      <ResultsScreen
        assessment={assessment}
        schema={schema}
        tenderId={tenderId}
        onRetake={() => setShowRetakeModal(true)}
        showRetakeModal={showRetakeModal}
        retakeConfirmed={retakeConfirmed}
        onCloseRetake={() => setShowRetakeModal(false)}
      />
    );
  }

  return null;
}

// =============================================================================
// Results screen — separate component so it can mount cleanly
// =============================================================================
function ResultsScreen({ assessment, schema, onRetake, showRetakeModal, retakeConfirmed, onCloseRetake, tenderId }) {
  const a = assessment;
  const pill = PILL_FOR(a.tier_color);
  const historyUrl = `/tenders/${tenderId}/bnbd-v2/history`;
  const historyData = useHistory(historyUrl, a.id);
  const { previous, attempts } = historyData;
  const prevCatMap = useMemo(() => buildPrevCategoryMap(previous, "id"), [previous]);
  // Red flags (BNBD = maturity direction → worst = lowest-point option)
  const questions = (schema?.questions || []);
  return (
    <div className="space-y-6 animate-fade-up" data-testid="bnbd-results">
      {/* Headline */}
      <div className={`card-flat p-8 border-l-4 ${pill.border}`} data-testid="bnbd-result-headline">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="stat-label flex items-center gap-3">
              BNBD result
              {attempts > 1 && <span className="chip border-ink-line bg-secondary text-ink-muted text-[10px] font-num" data-testid="bnbd-attempts-chip">Attempt {attempts}</span>}
            </div>
            <div className="flex items-baseline gap-2 mt-2">
              <div className="font-num text-7xl font-bold leading-none">{a.total_score}</div>
              <div className="text-2xl font-num text-ink-muted">/ 100</div>
            </div>
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <span className={`px-3 py-1 text-sm font-display font-bold rounded-sm border ${pill.bg} ${pill.text} ${pill.border}`} data-testid="bnbd-tier-chip">
                {a.tier_label}
              </span>
              <span className="font-display text-2xl font-bold tracking-tight" data-testid="bnbd-action">
                {a.action}
              </span>
            </div>
            <HistoryDeltaHeadline historyUrl={historyUrl} historyData={historyData} currentId={a.id} currentScore={a.total_score} direction="maturity" testid="bnbd-history-delta" />
          </div>
          <button onClick={onRetake} data-testid="bnbd-result-retake-btn" className="px-4 py-2 text-sm border border-ink-line hover:bg-secondary rounded-sm flex items-center gap-2 transition-colors">
            <RotateCcw className="w-4 h-4" /> Retake
          </button>
        </div>
      </div>

      {/* Category breakdown */}
      <div className="card-flat p-6">
        <div className="stat-label">Category breakdown</div>
        <div className="space-y-3 mt-4" data-testid="bnbd-breakdown">
          {(a.category_breakdown || []).map((c) => {
            const pct = Math.round((c.score / c.max_score) * 100);
            const flagPill = PILL_FOR(c.flag_color);
            const prev = prevCatMap[c.id];
            return (
              <div key={c.id} className="space-y-1" data-testid={`bnbd-cat-${c.id}`}>
                <div className="flex items-baseline justify-between text-sm">
                  <span className="font-medium">{c.label}</span>
                  <span className="flex items-center gap-2">
                    <span className="font-num text-ink-muted" data-testid={`bnbd-cat-pct-${c.id}`}>{pct}%</span>
                    {prev && <CategoryDelta prevScore={prev.score} prevMax={prev.max} curScore={c.score} curMax={c.max_score} direction="maturity" testid={`bnbd-cat-delta-${c.id}`} />}
                    <span className={`chip text-[10px] ${flagPill.bg} ${flagPill.text} ${flagPill.border}`}>{c.flag_label}</span>
                  </span>
                </div>
                <div className="h-2 bg-secondary rounded-sm overflow-hidden">
                  <div className={`h-full transition-all ${BAR_FOR(c.flag_color)}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Risk concentration */}
      {a.risk_concentration && (
        <div className="card-flat p-5 border-l-4 border-warn/40 bg-warn-soft/40 flex items-start gap-3" data-testid="bnbd-risk-note">
          <AlertTriangle className="w-4 h-4 text-warn shrink-0 mt-0.5" />
          <div className="text-sm text-ink leading-relaxed">
            {a.risk_concentration.split("**").map((part, i) =>
              i % 2 === 1 ? <strong key={i}>{part}</strong> : <span key={i}>{part}</span>
            )}
          </div>
        </div>
      )}

      <div className="text-[11px] uppercase tracking-wider text-ink-muted">
        Completed {a.completed_at ? new Date(a.completed_at).toLocaleString() : "—"}
      </div>

      {/* Red flags — worst-outcome (lowest-point) answers, per tesm_redflags_lock_bidscore_spec.md §2 */}
      <RedFlagSummary questions={questions} answers={a.answers || {}} direction="maturity"
        questionKeyFn={(q) => String(q.num)} testidPrefix="bnbd-redflag" />

      {showRetakeModal && (
        <RetakeModal
          previousScore={a.total_score}
          previousTier={a.tier_label}
          onConfirm={retakeConfirmed}
          onClose={onCloseRetake}
        />
      )}
    </div>
  );
}

function RetakeModal({ previousScore, previousTier, onConfirm, onClose }) {
  return (
    <div className="fixed inset-0 bg-ink/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="bnbd-retake-modal">
      <div className="bg-white max-w-md w-full border border-ink-line rounded-sm" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-ink-line">
          <div className="font-display font-bold tracking-tight">Retake BNBD assessment?</div>
        </div>
        <div className="p-6 text-sm space-y-3 text-ink-muted">
          <p>The current attempt will be archived and a fresh assessment will start.</p>
          {Number.isFinite(previousScore) && (
            <p>
              Previous result: <strong className="text-ink font-num">{previousScore} / 100</strong>
              {previousTier && <> · <strong className="text-ink">{previousTier}</strong></>}
            </p>
          )}
        </div>
        <div className="px-6 py-4 border-t border-ink-line flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-ink-line hover:bg-secondary rounded-sm transition-colors" data-testid="bnbd-retake-cancel">Cancel</button>
          <button onClick={onConfirm} className="px-5 py-2 text-sm bg-brand hover:bg-brand-hover text-white font-medium rounded-sm flex items-center gap-2 transition-colors" data-testid="bnbd-retake-confirm">
            <RotateCcw className="w-4 h-4" /> Retake
          </button>
        </div>
      </div>
    </div>
  );
}
